import { useAuthorizedRequest } from '@/hooks/useRequest';
import { apiRequest } from '@/lib/api';
import type { PaymentAttributes, PaymentBy } from '@/lib/Payment';
import { t } from '@/lib/i18n';
import {
    Box, Button, CloseButton, Drawer, Heading, HStack, Input, Portal, Table, Text, Tooltip, VStack,
} from '@chakra-ui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function jsonFetcher(url: string, token: string) {
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
}

function nowLocal(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const selectStyle = {
    width: '100%', padding: '4px 8px', borderRadius: '4px',
    border: '1px solid #ccc', fontSize: '14px', background: 'transparent',
};

interface StudentWithClient {
    id: string;
    client_id: string;
    client_name: string;
    name: string;
    date_of_birth?: string;
    comment?: string;
    deleted: boolean;
    created_at: string;
    updated_at: string;
}

/** Drawer for creating a payment for a specific client from the Students page. */
function StudentPaymentDrawer({ clientId, clientName, open, onClose, onSave, getAccessToken }: {
    clientId: string;
    clientName: string;
    open: boolean;
    onClose: () => void;
    onSave: (attrs: PaymentAttributes) => Promise<string | undefined>;
    getAccessToken: () => Promise<string>;
}) {
    const [amount, setAmount] = useState('');
    const [dateTime, setDateTime] = useState(nowLocal());
    const [comment, setComment] = useState('');
    const [paymentBy, setPaymentBy] = useState<PaymentBy>('cash');
    const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    async function handleSubmit() {
        const amt = parseInt(amount, 10);
        if (isNaN(amt) || amt < 0) { setError(t('payments.amount_invalid')); return; }
        setSubmitting(true);
        setError('');
        try {
            const paymentId = await onSave({
                client_id: clientId,
                amount: amt,
                date_time: new Date(dateTime).toISOString(),
                comment: comment.trim() || undefined,
                payment_by: paymentBy,
                deleted: false,
            });
            if (paymentId && attachmentFile) {
                const token = await getAccessToken();
                const res = await apiRequest<{ upload_url: string }>('POST', `/payments/${paymentId}/attachment/upload`, token);
                await fetch(res.upload_url, { method: 'PUT', body: attachmentFile });
            }
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : t('error.unknown'));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Drawer.Root open={open} onOpenChange={e => { if (!e.open) onClose(); }} placement="end">
            <Portal>
                <Drawer.Backdrop />
                <Drawer.Positioner>
                    <Drawer.Content>
                        <Drawer.Header>
                            <Drawer.Title>{t('payments.new')} — {clientName}</Drawer.Title>
                        </Drawer.Header>
                        <Drawer.Body>
                            <VStack gap={3} align="stretch">
                                {error && <Text color="red.500" fontSize="sm">{error}</Text>}
                                <Box>
                                    <Text fontSize="sm" mb="1">{t('label.client')}</Text>
                                    <Text fontSize="md" fontWeight="medium">{clientName}</Text>
                                </Box>
                                <Box>
                                    <Text fontSize="sm" mb="1">{t('label.amount')} (₴) *</Text>
                                    <Input size="sm" type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)} />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" mb="1">{t('label.date')} *</Text>
                                    <Input size="sm" type="datetime-local" value={dateTime} onChange={e => setDateTime(e.target.value)} />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" mb="1">{t('label.payment_by')} *</Text>
                                    <select value={paymentBy} onChange={e => setPaymentBy(e.target.value as PaymentBy)} style={selectStyle}>
                                        <option value="cash">{t('payment_by.cash')}</option>
                                        <option value="card">{t('payment_by.card')}</option>
                                    </select>
                                </Box>
                                <Box>
                                    <Text fontSize="sm" mb="1">{t('label.comment')}</Text>
                                    <Input size="sm" value={comment} onChange={e => setComment(e.target.value)} />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" mb="1">{t('label.receipt')}</Text>
                                    <input type="file" accept="image/*" hidden id="student-payment-attachment" onChange={e => setAttachmentFile(e.target.files?.[0] ?? null)} />
                                    <Button size="xs" variant="outline" onClick={() => document.getElementById('student-payment-attachment')?.click()}>
                                        {attachmentFile ? attachmentFile.name : t('action.choose_file')}
                                    </Button>
                                    {attachmentFile && (
                                        <Button size="xs" variant="ghost" ml={2} onClick={() => setAttachmentFile(null)}>{t('action.clear')}</Button>
                                    )}
                                </Box>
                            </VStack>
                        </Drawer.Body>
                        <Drawer.Footer>
                            <HStack>
                                <Button size="sm" onClick={handleSubmit} loading={submitting}>{t('action.save')}</Button>
                                <Button size="sm" variant="ghost" onClick={onClose}>{t('action.cancel')}</Button>
                            </HStack>
                        </Drawer.Footer>
                        <Drawer.CloseTrigger asChild>
                            <CloseButton size="xs" variant="solid" />
                        </Drawer.CloseTrigger>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    );
}

export function Students() {
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const [filter, setFilter] = useState('');
    const [paymentDrawerStudent, setPaymentDrawerStudent] = useState<StudentWithClient | null>(null);

    const { data, error, isLoading, isAuthenticated, mutate } =
        useAuthorizedRequest<{ students: StudentWithClient[] }>('/students', jsonFetcher);

    const students = useMemo(() => {
        return (data?.students ?? [])
            .filter(s => {
                if (!filter) return true;
                const q = filter.toLowerCase();
                return s.name.toLowerCase().includes(q)
                    || (s.client_name && s.client_name.toLowerCase().includes(q))
                    || s.id.toLowerCase().includes(q);
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [data, filter]);

    async function handleCreatePayment(attrs: PaymentAttributes): Promise<string | undefined> {
        const token = await getAccessTokenSilently();
        const result = await apiRequest<{ payment: { id: string } }>('POST', '/payments', token, attrs);
        await mutate();
        return result?.payment?.id;
    }

    if (!isAuthenticated) return <div>{t('label.needs_auth')}</div>;
    if (error) return <div>{t('label.error')} {t('students.title')}: {(error as Error).message}</div>;

    return (
        <VStack gap={4} align="stretch">
            <Heading size="lg">{t('students.title')}</Heading>

            <HStack>
                <Input
                    placeholder={t('students.search')}
                    variant="subtle"
                    flex="1"
                    onChange={(e) => setFilter(e.target.value)}
                />
            </HStack>

            <Table.Root variant="line" showColumnBorder interactive size="sm">
                <Table.Header>
                    <Table.Row>
                        <Table.ColumnHeader>{t('label.name')}</Table.ColumnHeader>
                        <Table.ColumnHeader>{t('label.client')}</Table.ColumnHeader>
                        <Table.ColumnHeader>{t('label.actions')}</Table.ColumnHeader>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {students.map((student) => (
                        <Table.Row key={student.id}>
                            <Table.Cell>{student.name}</Table.Cell>
                            <Table.Cell>{student.client_name}</Table.Cell>
                            <Table.Cell>
                                <HStack gap={2}>
                                    <Tooltip.Root>
                                        <Tooltip.Trigger asChild>
                                            <Button
                                                size="xs"
                                                variant="outline"
                                                onClick={() => navigate('/lessons', {
                                                    state: {
                                                        studentId: student.id,
                                                        clientId: student.client_id,
                                                        studentName: student.name,
                                                        clientName: student.client_name,
                                                    },
                                                })}
                                            >
                                                📚
                                            </Button>
                                        </Tooltip.Trigger>
                                        <Tooltip.Positioner>
                                            <Tooltip.Content>{t('students.add_lesson')}</Tooltip.Content>
                                        </Tooltip.Positioner>
                                    </Tooltip.Root>
                                    <Tooltip.Root>
                                        <Tooltip.Trigger asChild>
                                            <Button
                                                size="xs"
                                                variant="outline"
                                                onClick={() => setPaymentDrawerStudent(student)}
                                            >
                                                💰
                                            </Button>
                                        </Tooltip.Trigger>
                                        <Tooltip.Positioner>
                                            <Tooltip.Content>{t('students.add_payment')}</Tooltip.Content>
                                        </Tooltip.Positioner>
                                    </Tooltip.Root>
                                </HStack>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table.Root>

            {paymentDrawerStudent && (
                <StudentPaymentDrawer
                    clientId={paymentDrawerStudent.client_id}
                    clientName={paymentDrawerStudent.client_name}
                    open={!!paymentDrawerStudent}
                    onClose={() => setPaymentDrawerStudent(null)}
                    onSave={handleCreatePayment}
                    getAccessToken={getAccessTokenSilently}
                />
            )}
        </VStack>
    );
}
